import React from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security, LoginCallback } from '@okta/okta-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { oktaConfig } from './config';
import { setOktaAuth } from './services/api';
import Layout from './components/Layout';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import SourceProfilingWizard from './pages/source-profiling/SourceProfilingWizard';
import SDQWizard from './pages/sdq/SDQWizard';
import UDQWizard from './pages/udq/UDQWizard';
import RuleCatalog from './pages/rules/RuleCatalog';
import ProjectsPage from './pages/projects/ProjectsPage';
import GroupsPage from './pages/groups/GroupsPage';
import InsightsPage from './pages/insights/InsightsPage';
import PDQPage from './pages/pdq/PDQPage';
import ProjectsLandingPage from './pages/observability/ProjectsLandingPage';
import ObservabilityPage from './pages/observability/ObservabilityPage';
import MappingPage from './pages/mapping/MappingPage';
import MappingRunDetail from './pages/mapping/MappingRunDetail';
// Per-persona agents (the legacy Workbench surface was retired)
import ProfilingAgentPage from './pages/source-profiling/agent';
import ProfilingAgentConversation from './pages/source-profiling/agent/ConversationPage';
import IncidentAnalystPage from './pages/observability/incident-analyst';
import IncidentAnalystConversation from './pages/observability/incident-analyst/ConversationPage';
import DataContentProfilerPage from './pages/data-content-profiler';
import DataContentProfilerConversation from './pages/data-content-profiler/Conversation';
import SchemaMappingAgentPage from './pages/schema-mapping-agent';
import SchemaMappingAgentConversation from './pages/schema-mapping-agent/Conversation';
// New Profiling Agent (Reltio-style flow) — isolated route, full-screen dark theme,
// uses its own sidebar (NOT the cargo-app Layout). Lives outside the Layout
// wrapper so the existing app chrome doesn't bleed into the Reltio experience.

const oktaAuth = new OktaAuth(oktaConfig);
setOktaAuth(oktaAuth);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

/* Wrapper that provides useNavigate to Security's restoreOriginalUri */
const AppWithRouter: React.FC = () => {
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login/callback" element={<LoginCallback />} />

        {/* Protected routes (with cargo-app Layout chrome) */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home />} />
          <Route path="/source-profiling" element={<SourceProfilingWizard />} />
          <Route path="/sdq" element={<SDQWizard />} />
          <Route path="/udq" element={<UDQWizard />} />
          <Route path="/pdq" element={<PDQPage />} />
          <Route path="/observability" element={<ProjectsLandingPage />} />
          <Route path="/observability/:projectId" element={<ObservabilityPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/rules" element={<RuleCatalog />} />
          <Route path="/reports" element={<InsightsPage />} />
          <Route path="/mapping" element={<MappingPage />} />
          <Route path="/mapping/runs/:runId" element={<MappingRunDetail />} />
          {/* Profiling Agent (onboarding surface) */}
          <Route path="/source-profiling/agent" element={<ProfilingAgentPage />} />
          <Route path="/source-profiling/agent/sessions/:sessionId" element={<ProfilingAgentConversation />} />
          {/* Data Content Profiler (standalone building-block agent) */}
          <Route path="/data-content-profiler" element={<DataContentProfilerPage />} />
          <Route path="/data-content-profiler/sessions/:sessionId" element={<DataContentProfilerConversation />} />
          <Route path="/schema-mapping-agent" element={<SchemaMappingAgentPage />} />
          <Route path="/schema-mapping-agent/sessions/:sessionId" element={<SchemaMappingAgentConversation />} />
          {/* Incident Analyst (investigation surface) */}
          <Route path="/observability/analyst" element={<IncidentAnalystPage />} />
          <Route path="/observability/analyst/sessions/:sessionId" element={<IncidentAnalystConversation />} />
          <Route path="/settings" element={<Placeholder title="Settings" />} />
        </Route>
      </Routes>
    </Security>
  );
};

/* Temporary placeholder for pages not yet built */
function Placeholder({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-cargo-text mb-2">{title}</h2>
        <p className="text-cargo-muted">Coming soon</p>
      </div>
    </div>
  );
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppWithRouter />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
